/**
 * src/components/public/layout/HeaderDropdown.tsx
 *
 * Desktop ana navigasyon — Deloitte tarzı three-pane mega menu.
 *
 * Layout:
 *   ┌──────────────────────────────────────────────────────────────┐
 *   │ Sol Sidebar (240px)│ Orta 3-col grid (1fr)    │ Featured     │
 *   │ • Hakkımızda       │ Alt sayfalar (hover)     │ (260px)      │
 *   │ → Sertifikalar     │ ─────────────────────────│ ┌──────────┐ │
 *   │ • Referanslar      │ Sub-1     Sub-2          │ │ gradient │ │
 *   │ • Politikalar      │ Sub-3     Sub-4          │ │ promo    │ │
 *   │                    │ Sub-5     Sub-6          │ │ + CTA    │ │
 *   │                    │                          │ └──────────┘ │
 *   └──────────────────────────────────────────────────────────────┘
 *
 * Davranış:
 *   - Sol sidebar item'ında hover → orta panel o item'ın children'ını gosterir
 *   - Aktif item: brand renkli, sol border'i brand renk
 *   - Item'ın children yoksa: orta panel'de büyük "Bu sayfaya git" CTA
 *   - Sağ featured kart: sabit brand promosyonu (locale-aware, gradient bg)
 *
 * Tasarım:
 *   - 920px wide (önceki 640px → daha geniş, three-pane için)
 *   - Light: beyaz bg, slate-50 sidebar hover, brand-700 aktif text
 *   - Dark: slate-950 bg, slate-800 hover, brand-300 aktif text
 *   - Profesyonel B2B kurumsal his
 */

'use client'

import { ArrowRight, ChevronDown, ChevronRight, ShieldCheck } from 'lucide-react'
import { useLocale, useTranslations } from 'next-intl'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import type { NavMenuItem } from '@/data/types/site-settings'
import { pickLocalized } from '@/domain/value-objects/localized-string'
import type { Locale } from '@/domain/value-objects/locale'
import { Link } from '@/lib/i18n/navigation'

interface HeaderDropdownProps {
  item: NavMenuItem
}

/** Panel'in trigger butonuna göre yatay hizalama. */
type PanelAlign = 'left' | 'center' | 'right'

/** Panel toplam genisliği (px). CSS w-[920px] ile senkron. */
const PANEL_WIDTH = 920

export function HeaderDropdown({ item }: HeaderDropdownProps) {
  const locale = useLocale() as Locale
  const tc = useTranslations('components.header_dropdown')
  const [open, setOpen] = useState(false)
  // Sol sidebar'da hover/active olan child'ın index'i. Default: ilk child.
  const [activeChildIdx, setActiveChildIdx] = useState(0)
  // Trigger butonun viewport'taki konumuna göre panel alignment.
  // 'center' default (ilk render); useEffect ile gercek konuma gore set edilir.
  const [alignment, setAlignment] = useState<PanelAlign>('center')
  const containerRef = useRef<HTMLDivElement | null>(null)
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  /** Trigger buton — Escape ile kapanışta fokusu buraya geri döndürmek için. */
  const triggerRef = useRef<HTMLButtonElement | null>(null)

  const label = pickLocalized(item.label, locale)
  const children = item.children ?? []
  // Aktif child — sol sidebar'dan secilen
  const activeChild = children[activeChildIdx]

  useEffect(() => {
    if (!open) return
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    function handleKey(e: KeyboardEvent) {
      if (e.key !== 'Escape') return
      setOpen(false)
      // Panel koşullu render'lı — kapanınca içindeki odaklı eleman DOM'dan
      // çıkar ve fokus <body>'ye düşerdi; klavye kullanıcısı sayfadaki yerini
      // kaybediyordu (WCAG 2.4.3 Focus Order). Fokusu trigger'a geri ver.
      triggerRef.current?.focus()
    }
    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('keydown', handleKey)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleKey)
    }
  }, [open])

  /**
   * Panel acildiginda viewport'a gore alignment hesaplar: trigger butonun
   * merkezi sag/sol kenara panel-half'tan yakinsa o tarafa hizali pozisyon
   * uygulanir (panel viewport'tan tasmaz). Aksi halde center.
   *
   * Resize listener da var: kullanici pencereyi degistirirse re-align.
   */
  /**
   * Trigger butonun viewport konumuna göre panel alignment'ını hesaplar (panel
   * kenardan taşmasın). Açılmadan ÖNCE (handleMouseEnter) çağrılır → panel doğru
   * hizada açılır, mount sonrası yeniden konumlanma/sıçrama olmaz.
   */
  const computeAlignment = useCallback((): PanelAlign => {
    const el = containerRef.current
    if (!el) return 'center'
    const rect = el.getBoundingClientRect()
    const buttonCenter = rect.left + rect.width / 2
    const panelHalf = PANEL_WIDTH / 2
    const SAFE = 16 // kenar güvenlik payı
    if (window.innerWidth - buttonCenter < panelHalf + SAFE) return 'right'
    if (buttonCenter < panelHalf + SAFE) return 'left'
    return 'center'
  }, [])

  // Click/klavye ile açılışta + pencere resize'inde re-align (hover yolu zaten
  // handleMouseEnter'da açılmadan önce hizalıyor).
  useEffect(() => {
    if (!open) return
    setAlignment(computeAlignment())
    const onResize = () => setAlignment(computeAlignment())
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [open, computeAlignment])

  function handleMouseEnter() {
    if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current)
    setAlignment(computeAlignment()) // açmadan önce hizala → sıçrama yok
    setOpen(true)
  }
  function handleMouseLeave() {
    closeTimeoutRef.current = setTimeout(() => setOpen(false), 150)
  }

  // Featured kart content — sabit promosyon, i18n messages'tan (yeni dil
  // eklenince messages/{locale}.json'a components.header_dropdown anahtarlari eklenir).
  const featured = useMemo(
    () => ({
      eyebrow: tc('featured'),
      title: tc('featured_title'),
      desc: tc('featured_desc'),
      cta: tc('get_assessment'),
      href: '/iletisim',
    }),
    [tc],
  )

  return (
    <div
      ref={containerRef}
      className="group relative"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <button
        ref={triggerRef}
        type="button"
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className={`relative inline-flex items-center whitespace-nowrap px-2.5 py-2 text-sm font-medium rounded-md transition-colors ${
          open
            ? 'text-slate-900 dark:text-white'
            : 'text-slate-700 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white'
        }`}
      >
        <span>{label}</span>
        {/* 🔴 Ok isareti AKISTAN CIKARILDI (absolute), yer kaplamiyor.
            Sebep: sekiz tetikleyicinin oku toplam ~80px eniyordu ve sigmadigimiz
            miktar tam da buydu (olcum 2026-08-15). Etiketleri kisaltmak veya
            baslik gruplamak yerine simgeyi akistan cikardik — SITE HARITASI'ndaki
            sekiz baslik oldugu gibi, yan yana kaliyor.
            Erisilebilirlik korunuyor: acilir menu bilgisini `aria-haspopup` ve
            `aria-expanded` tasiyor, ok yalnizca gorsel bir ipucuydu. Simge
            hover/acik durumda altta beliriyor, boylece ipucu da kaybolmuyor. */}
        <ChevronDown
          size={12}
          className={`pointer-events-none absolute left-1/2 -translate-x-1/2 bottom-0 transition-opacity duration-200 ${
            open ? 'opacity-70 rotate-180' : 'opacity-0 group-hover:opacity-40'
          }`}
          aria-hidden
        />
      </button>

      {open && children.length > 0 && (
        <>
          {/* Hover gap koruyucu */}
          <div aria-hidden className="absolute top-full inset-x-0 h-2" />

          <div
            role="menu"
            aria-label={label}
            className={`absolute top-full mt-2 z-50 ${
              alignment === 'right'
                ? 'right-0'
                : alignment === 'left'
                  ? 'left-0'
                  : 'left-1/2 -translate-x-1/2'
            }`}
          >
            {/* Animasyon İÇ panelde: ortalama transform'u (dış div'deki
                -translate-x-1/2) ezmesin → açılışta sıçrama/takılma olmaz. */}
            <div className="animate-dropdown-in rounded-2xl bg-white dark:bg-slate-950 ring-1 ring-slate-200 dark:ring-slate-800 shadow-[0_20px_50px_-12px_rgba(0,0,0,0.15)] dark:shadow-[0_20px_50px_-12px_rgba(0,0,0,0.6)] overflow-hidden w-[920px] max-w-[92vw]">
              <div className="grid grid-cols-[260px_1fr_260px] max-h-[calc(100vh-7rem)]">
                {/* ─── SOL SIDEBAR ─── */}
                <nav
                  aria-label={`${label} kategorileri`}
                  className="border-r border-slate-100 dark:border-slate-800 bg-slate-50/60 dark:bg-slate-900/40 p-3 overflow-y-auto"
                >
                  <ul className="space-y-0.5">
                    {children.map((child, idx) => {
                      const childLabel = pickLocalized(child.label, locale)
                      const isActive = idx === activeChildIdx
                      const hasSubs = (child.children?.length ?? 0) > 0
                      // Click davranisi: child.href varsa direkt yonlendir
                      // (kullanici geri bildirimi 2026-05-29 — mega menu link'i
                      // tikladiginda sayfaya gitsin, "Sayfaya git" CTA'sina
                      // gerek olmasin). href yoksa button kalir, hover ile
                      // orta panel aktif olur.
                      const baseClassName = `group w-full flex items-start justify-between gap-2 px-3 py-2.5 rounded-lg text-left text-sm leading-snug transition-colors ${
                        isActive
                          ? 'bg-white dark:bg-slate-800 text-brand-700 dark:text-brand-300 font-semibold shadow-sm'
                          : 'text-slate-700 dark:text-slate-300 hover:bg-white/70 dark:hover:bg-slate-800/60 hover:text-slate-900 dark:hover:text-white'
                      }`
                      const inner = (
                        <>
                          {/* whitespace-normal: uzun label wrap olur (truncate yok) */}
                          <span className="flex-1 min-w-0 whitespace-normal break-words">
                            {childLabel}
                          </span>
                          {hasSubs && (
                            <ChevronRight
                              size={14}
                              aria-hidden
                              className={`flex-shrink-0 mt-1 transition-opacity ${
                                isActive
                                  ? 'opacity-100 text-brand-700 dark:text-brand-300'
                                  : 'opacity-0 group-hover:opacity-60'
                              }`}
                            />
                          )}
                        </>
                      )
                      return (
                        <li key={`sb-${child.href ?? idx}`}>
                          {child.href ? (
                            <Link
                              href={child.href}
                              onMouseEnter={() => setActiveChildIdx(idx)}
                              onFocus={() => setActiveChildIdx(idx)}
                              onClick={() => setOpen(false)}
                              className={baseClassName}
                            >
                              {inner}
                            </Link>
                          ) : (
                            <button
                              type="button"
                              onMouseEnter={() => setActiveChildIdx(idx)}
                              onFocus={() => setActiveChildIdx(idx)}
                              className={baseClassName}
                            >
                              {inner}
                            </button>
                          )}
                        </li>
                      )
                    })}
                  </ul>
                </nav>

                {/* ─── ORTA PANEL ─── */}
                <div className="p-6 overflow-y-auto">
                  {activeChild && (
                    <ActiveChildPanel
                      child={activeChild}
                      locale={locale}
                      onNavigate={() => setOpen(false)}
                    />
                  )}
                </div>

                {/* ─── SAG FEATURED KART ─── */}
                <aside
                  aria-label={featured.eyebrow}
                  className="bg-gradient-to-br from-brand-700 to-brand-900 dark:from-brand-800 dark:to-brand-950 text-white p-6 flex flex-col justify-between relative overflow-hidden"
                >
                  {/* Dekoratif glow — radial-gradient (CSS `blur-3xl` filter
                      yerine). Perf (kullanıcı geri bildirimi 2026-06-04 "mega
                      menü açılırken takılma"): blur-3xl, 160px elemana 64px blur
                      filtresi uygular; mega menü `animate-dropdown-in` ile
                      açılırken bu filtre her frame yeniden hesaplanıp GPU hitch
                      yaratıyordu. radial-gradient aynı yumuşak glow'u filter
                      maliyeti OLMADAN verir (tek seferlik paint). */}
                  <div
                    aria-hidden
                    className="absolute -top-20 -right-20 w-56 h-56 rounded-full bg-[radial-gradient(circle,rgba(245,158,11,0.30)_0%,transparent_70%)]"
                  />
                  <div className="relative z-10">
                    <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/15 backdrop-blur border border-white/20 text-[10px] font-bold uppercase tracking-wider text-white mb-4">
                      <ShieldCheck size={11} aria-hidden />
                      {featured.eyebrow}
                    </div>
                    <h3 className="text-base font-bold leading-tight mb-2">{featured.title}</h3>
                    <p className="text-xs text-brand-100 leading-relaxed mb-5">{featured.desc}</p>
                  </div>
                  <Link
                    href={featured.href}
                    onClick={() => setOpen(false)}
                    className="relative z-10 inline-flex items-center justify-center gap-1.5 w-full px-4 py-2.5 rounded-lg bg-accent-500 hover:bg-accent-600 text-white font-semibold text-sm transition-colors shadow-lg shadow-accent-500/30"
                  >
                    {featured.cta}
                    <ArrowRight size={14} aria-hidden />
                  </Link>
                </aside>
              </div>

              {/* Footer CTA — parent kendi href'i varsa */}
              {item.href && (
                <div className="border-t border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/50 px-6 py-3">
                  <Link
                    href={item.href}
                    onClick={() => setOpen(false)}
                    className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand-700 dark:text-brand-300 hover:text-brand-800 dark:hover:text-brand-200"
                  >
                    {tc('all_x_services', { label })}
                    <ArrowRight size={14} aria-hidden />
                  </Link>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  )
}

/**
 * ActiveChildPanel — orta panel content. Aktif child'ın children'i varsa
 * 2-col grid; yoksa promo CTA (büyük tek link).
 */
function ActiveChildPanel({
  child,
  locale,
  onNavigate,
}: {
  child: NavMenuItem
  locale: Locale
  onNavigate: () => void
}) {
  const tc = useTranslations('components.header_dropdown')
  const childLabel = pickLocalized(child.label, locale)
  const subs = child.children ?? []

  if (subs.length === 0) {
    // Children yok — büyük "go to page" CTA
    if (!child.href) {
      return <div className="text-sm text-slate-500 dark:text-slate-400">{tc('no_items')}</div>
    }
    return (
      <div className="flex flex-col h-full justify-center">
        <h3 className="text-2xl font-bold text-slate-900 dark:text-slate-100 mb-3">{childLabel}</h3>
        <p className="text-sm text-slate-600 dark:text-slate-400 mb-6 max-w-md">
          {tc('discover_template', { name: childLabel.toLowerCase() })}
        </p>
        <Link
          href={child.href}
          onClick={onNavigate}
          className="inline-flex items-center gap-2 self-start px-4 py-2.5 rounded-lg bg-brand-700 hover:bg-brand-800 dark:bg-brand-600 dark:hover:bg-brand-500 text-white font-semibold text-sm transition-colors"
        >
          {tc('go_to_page')}
          <ArrowRight size={14} aria-hidden />
        </Link>
      </div>
    )
  }

  // Children var — başlık + "Tümüne bak" + 2-col grid
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
          {childLabel}
        </h3>
        {child.href && (
          <Link
            href={child.href}
            onClick={onNavigate}
            className="text-[11px] font-semibold text-brand-700 dark:text-brand-300 hover:text-brand-800 dark:hover:text-brand-200 inline-flex items-center gap-1"
          >
            {tc('view_all')}
            <ArrowRight size={11} aria-hidden />
          </Link>
        )}
      </div>
      <ul className="grid grid-cols-2 gap-1">
        {subs.map((sub, idx) => (
          <li key={`sub-${sub.href ?? idx}`}>
            <SubLink item={sub} locale={locale} onClick={onNavigate} />
          </li>
        ))}
      </ul>
    </div>
  )
}

/**
 * SubLink — orta panel'deki tek link. Bold label + hover slide chevron.
 */
function SubLink({
  item,
  locale,
  onClick,
}: {
  item: NavMenuItem
  locale: Locale
  onClick: () => void
}) {
  const label = pickLocalized(item.label, locale)

  if (!item.href) {
    return <div className="px-3 py-2 text-sm text-slate-400 dark:text-slate-600">{label}</div>
  }

  return (
    <Link
      href={item.href}
      onClick={onClick}
      role="menuitem"
      className="group relative flex items-start gap-2 px-3 py-2 rounded-lg text-sm leading-snug font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800/60 hover:text-slate-900 dark:hover:text-white transition-colors"
    >
      {/* whitespace-normal: uzun servis adlari wrap olur (truncate yok) */}
      <span className="flex-1 min-w-0 whitespace-normal break-words">{label}</span>
      <span
        aria-hidden
        className="opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all mt-1 text-slate-400 dark:text-slate-500 text-xs flex-shrink-0"
      >
        →
      </span>
    </Link>
  )
}
